/**
 * events.js — v1.0.0 Gate 3
 *
 * 15 PostHog events exactly. Names are permanent. Snake_case.
 * Each function fires the event at the exact moment the action occurs.
 * Routes through existing trackEvent() → PostHog + Mixpanel + backend activity log.
 */
import { trackEvent } from "@/lib/track";

// 1. app_opened — fires on app mount
export function trackAppOpened(userState, daysUntilBirthday) {
  trackEvent("app_opened", {
    user_state: userState || "unknown",
    days_until_birthday: daysUntilBirthday,
    platform: /android/i.test(navigator.userAgent) ? "android" : /iphone|ipad/i.test(navigator.userAgent) ? "ios" : "web",
  });
}

// 2. offer_viewed — fires when user opens an offer detail page
export function trackOfferViewed(businessId, offerType, listingType, userState, sourceTab) {
  trackEvent("offer_viewed", {
    business_id: businessId,
    offer_type: offerType || "unknown",
    listing_type: listingType || "standard",
    user_state: userState || "unknown",
    source_tab: sourceTab || "home",
  });
}

// 3. offer_saved — fires when user saves/favorites an offer
export function trackOfferSaved(businessId, offerType, userState) {
  trackEvent("offer_saved", {
    business_id: businessId,
    offer_type: offerType || "unknown",
    user_state: userState || "unknown",
  });
}

// 4. offer_unsaved — fires when user removes a saved offer
export function trackOfferUnsaved(businessId) {
  trackEvent("offer_unsaved", {
    business_id: businessId,
  });
}

// 5. search_performed — fires when user submits a search
export function trackSearchPerformed(queryLength, filtersApplied, resultCount) {
  trackEvent("search_performed", {
    query_length: queryLength,
    filters_applied: filtersApplied,
    result_count: resultCount,
  });
}

// 6. filter_applied — fires when user applies a filter
export function trackFilterApplied(filterType, filterValue) {
  trackEvent("filter_applied", {
    filter_type: filterType,
    filter_value: filterValue,
  });
}

// 7. birthday_countdown_tapped — fires when user taps the countdown in greeting card
export function trackBirthdayCountdownTapped(daysRemaining, userState) {
  trackEvent("birthday_countdown_tapped", {
    days_remaining: daysRemaining,
    user_state: userState || "unknown",
  });
}

// 8. greeting_displayed — fires when greeting engine renders
export function trackGreetingDisplayed(userState, greetingVariant) {
  trackEvent("greeting_displayed", {
    user_state: userState || "unknown",
    greeting_variant: greetingVariant || "unknown",
  });
}

// 9. contribution_submitted — fires when user submits any contribution
export function trackContributionSubmitted(contributionType) {
  trackEvent("contribution_submitted", {
    contribution_type: contributionType,
  });
}

// 10. points_earned — fires when points are awarded
export function trackPointsEarned(ruleId, pointsAmount, triggerEvent, totalPointsAfter) {
  trackEvent("points_earned", {
    rule_id: ruleId,
    points_amount: pointsAmount,
    trigger_event: triggerEvent,
    total_points_after: totalPointsAfter,
  });
}

// 11. badge_earned — fires when a badge is awarded
export function trackBadgeEarned(badgeId, badgeName, triggerEvent) {
  trackEvent("badge_earned", {
    badge_id: badgeId,
    badge_name: badgeName,
    trigger_event: triggerEvent,
  });
}

// 12. post_birthday_survey_started — fires when survey opens
export function trackPostBirthdaySurveyStarted(daysAfterBirthday) {
  trackEvent("post_birthday_survey_started", {
    days_after_birthday: daysAfterBirthday,
  });
}

// 13. post_birthday_survey_completed — fires when survey is submitted
export function trackPostBirthdaySurveyCompleted(daysAfterBirthday, questionsAnswered) {
  trackEvent("post_birthday_survey_completed", {
    days_after_birthday: daysAfterBirthday,
    questions_answered: questionsAnswered,
  });
}

// 14. wanted_promo_added — fires when user adds a business to wanted promos
export function trackWantedPromoAdded(businessId, businessType, source) {
  trackEvent("wanted_promo_added", {
    business_id: businessId,
    business_type: businessType || "unknown",
    source: source || "manual",
  });
}

// 15. what_is_new_item_viewed — fires when user taps a What's New item
export function trackWhatsNewItemViewed(itemId, badgeType, daysSincePublished) {
  trackEvent("what_is_new_item_viewed", {
    item_id: itemId,
    badge_type: badgeType || "unknown",
    days_since_published: daysSincePublished,
  });
}
